<script lang="ts" setup>
import { reactive, computed, onMounted } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { email, required, minLength } from '@vuelidate/validators'
import BsButton from '@/legacy/components/Button.vue'
import BsDialogFull from '@/legacy/components/DialogFull.vue'
import BsTextFieldOutline from '@/legacy/components/TextFieldOutline.vue'
import { createCustomEvent, createGAEvent } from '@/assets/ts/customEvent'
import loginHelper from '@/assets/ts/loginHelper'
import useHashQuery from '@/legacy/ts/useHashQuery'
import { Spin } from 'ant-design-vue'
import buttonGoogle from '@/assets/images/button_google@2x.png'
import buttonFb from '@/assets/images/button_fb@2x.png'
import { instanceBuyandship } from '@/assets/ts/apiManagement'
import InApp from 'detect-inapp'

const props = defineProps({
  text: {
    type: Object,
  },
  isSupportEmailLogin: {
    type: Boolean,
    default: false,
  },
  isSupportFacebookLogin: {
    type: Boolean,
    default: false,
  },
  isSupportGoogleLogin: {
    type: Boolean,
    default: false,
  },
})

const state = reactive({
  isShowPanel: true,
  isLoading: false,
  isShowPassword: false,
  errorMessage: '',
  email: '',
  password: '',
})

const rules = {
  email: {
    required,
    email,
  },
  password: {
    required,
    minLength: minLength(6),
  },
}

const $v = useVuelidate(rules, state)
const { searchParameter, hashParameter } = useHashQuery()

const emailValidMsg = computed(() => {
  let msg = ''
  if ($v.value.email.$dirty) {
    if ($v.value.email.required.$invalid) {
      msg = props.text?.email.required
    } else if ($v.value.email.email.$invalid) {
      msg = props.text?.email.invalid
    }
  }
  return msg
})

const passwordValidMsg = computed(() => {
  let msg = ''
  if ($v.value.password.$dirty) {
    if ($v.value.password.required.$invalid) {
      msg = props.text?.password.required
    } else if ($v.value.password.minLength.$invalid) {
      msg = props.text?.password.min
    }
  }
  return msg
})

const passswordInputType = computed(() => {
  return state.isShowPassword ? 'text' : 'password'
})

const loginStatus = computed(() => {
  const status = searchParameter.value.find((query) => query.key === 'status')
  return status === undefined ? '' : status.val
})

const formatHashParameter: any = computed(() => {
  return hashParameter.value.length
    ? hashParameter.value[0]
        .substring(1)
        .split('&')
        .map((v) => v.split('='))
        .reduce((pre, [key, value]) => ({ ...pre, [key]: value }), {})
    : {}
})

const googleToken = computed(() => {
  return (
    Object.keys(formatHashParameter.value).length &&
    (formatHashParameter.value.access_token || null)
  )
})

const googleIcon = computed(() => {
  if (import.meta.env.DEV) {
    return { backgroundImage: `url(${buttonGoogle.src})` }
  }
  return ''
})

const fbIcon = computed(() => {
  if (import.meta.env.DEV) {
    return { backgroundImage: `url(${buttonFb.src})` }
  }
  return ''
})

function showSignup() {
  createGAEvent('c_signup_f_login')
  const payload = {
    type: 'show',
    data: 'signup',
  }
  window.postMessage(payload, window.location.origin)
}

function showForgetPassword() {
  createGAEvent('c_forgot_password_f_login')
  const payload = {
    type: 'show',
    data: 'forgetPassword',
  }
  window.postMessage(payload, window.location.origin)
}

function hidePanel() {
  state.isShowPanel = false
  const payload = {
    type: 'hide',
    data: 'login',
  }
  localStorage.removeItem('loginRedirectValue')
  window.postMessage(payload, window.location.origin)
}

function login() {
  if ($v.value.$invalid) return
  loginHelper.clearToken()
  state.isLoading = true

  createGAEvent('c_email_login_f_login')
  instanceBuyandship
    .post('sessions', {
      email: state.email,
      password: state.password,
      provider: 'basic',
    })
    .then(({ data }) => {
      window.BsHeaderVm.login({ ...eventParameterFormat(data), provider: 'basic' })
      state.errorMessage = ''
      hidePanel()
    })
    .catch((err) => {
      let message = 'System is busy, please try later'
      if (
        err.response &&
        err.response.data &&
        err.response.data.error &&
        err.response.data.error.message
      ) {
        message = err.response.data.error.message
      }
      state.errorMessage = message
    })
    .finally(() => (state.isLoading = false))
}

function requestFacebookSDK() {
  if (interceptInAppRequest()) {
    return
  }

  handleFacebookLogin()
}

function interceptInAppRequest() {
  const inapp = new InApp(
    navigator.userAgent || navigator.vendor || window.opera,
  )


  if (inapp.isInApp && inapp.isMobile) {
    const REDIRECT_LINK = 'https://qr.page/g/tSLYydLM2e'

    window.location.href = REDIRECT_LINK
    return true
  }

  return false
}

function handleFacebookLogin() {
  createGAEvent('c_facebook_login_f_login')
  state.isLoading = true

  loginHelper
    .requestFacebookSDK()
    .then((res: any) => {
      state.errorMessage = ''
      window.BsHeaderVm.login({
        ...eventParameterFormat(res),
        provider: 'facebook',
      })
      hidePanel()
    })
    .catch(({ message }: { message: string }) => {
      state.errorMessage = message
    })
    .finally(() => {
      state.isLoading = false
    })
}

function googleSignIn() {
  createGAEvent('c_google_login_f_login')
  loginHelper.googleSignIn()
}

function onGoogleSignIn() {
  if (!googleToken.value) {
    state.errorMessage = 'Can not get user token'
    return
  }
  state.isLoading = true
  loginHelper.clearToken()
  loginHelper
    .requestLogin({
      token: googleToken.value,
      provider: 'google',
    })
    .then(({ data }) => {
      const responseData = data.data || {}
      if (responseData.new_account && responseData.new_account === 'yes') {
        createCustomEvent('member_signed_up', { provider: 'google' })
      }
      state.errorMessage = ''
      window.BsHeaderVm.login({
        ...eventParameterFormat(responseData),
        provider: 'google',
      })
      hidePanel()
    })
    .catch(({ error }) => {
      state.errorMessage = error
    })
    .finally(() => {
      state.isLoading = false
    })
}

function eventParameterFormat(obj: any) {
  return Object.keys(obj).length
    ? Object.keys(obj).reduce((acc, key) => {
        if (key !== 'user_token') {
          // @ts-ignore
          acc[key] = obj[key]
        }
        return acc
      }, {})
    : {}
}

onMounted(() => {
  if (loginStatus.value === 'google-login-success') onGoogleSignIn()
})
</script>

<template>
  <BsDialogFull v-model="state.isShowPanel" class="login" @on-hide="hidePanel">
    <template #content>
      <div class="login-container bs-login">
        <div class="login-title text-h5">{{ text?.title }}</div>
        <div class="login-subtitle">{{ text?.subtitle }}</div>
        <template v-if="isSupportEmailLogin">
          <BsTextFieldOutline
            v-model="$v.email.$model"
            :placeholder="text?.email.placeholder"
            :isError="$v.email.$error"
            :errorMessage="emailValidMsg"
          >
            <template #trailing>
              <i class="icon-mail"></i>
            </template>
          </BsTextFieldOutline>
          <BsTextFieldOutline
            v-model="$v.password.$model"
            :type="passswordInputType"
            :placeholder="text?.password.placeholder"
            :isError="$v.password.$error"
            :errorMessage="passwordValidMsg"
          >
            <template #trailing>
              <i
                :class="[state.isShowPassword ? 'icon-eye-on' : 'icon-eye-off']"
                @click="state.isShowPassword = !state.isShowPassword"
              ></i>
            </template>
          </BsTextFieldOutline>
        </template>
        <template v-if="isSupportEmailLogin">
          <div class="login-button-submit">
            <Spin :spinning="state.isLoading">
              <bs-button
                class="full-width"
                :class="{ 'bs-button--disabled': $v.$invalid }"
                type="primary"
                solid
                @click="login"
              >
                {{ text?.button.login }}
              </bs-button>
            </Spin>
            <div class="login-action-message" v-show="state.errorMessage">
              {{ state.errorMessage }}
            </div>
          </div>
          <a class="login-forget" @click.prevent="showForgetPassword">{{ text?.forgot }}</a>
        </template>
        <div class="login-separator">
          <span class="login-separator-center">{{ text?.or }}</span>
        </div>
        <div class="login-signup">
          {{ text?.customer.prefix }}
          <a @click.prevent="showSignup">{{ text?.customer.suffix }}</a>
        </div>
        <template v-if="isSupportGoogleLogin">
          <div class="bs-field-group">
            <div class="bs-field__button bs-field__button--social" @click="googleSignIn">
              <span class="bs-field__button__leading">
                <i class="signup-icon singup-icon--google" :style="googleIcon"></i>
                <span class="bs-field__button__leading__text">{{ text?.google.button }}</span>
              </span>
              <i class="signup-icon singup-icon--arrowRight"></i>
            </div>
          </div>
        </template>
        <template v-if="isSupportFacebookLogin">
          <div class="bs-field-group">
            <div class="bs-field__button bs-field__button--social" @click="requestFacebookSDK">
              <span class="bs-field__button__leading">
                <i class="signup-icon singup-icon--facebook" :style="fbIcon"></i>
                <span class="bs-field__button__leading__text">{{ text?.facebook.button }}</span>
              </span>
              <i class="signup-icon singup-icon--arrowRight"></i>
            </div>
          </div>
        </template>
      </div>
    </template>
  </BsDialogFull>
</template>
